import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "download-timeline-modal-content__main-content main-content" }

import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { i18n } from '@/i18n';
import { computed, ref, watch } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE } from '../constants';
import { YearMonthRange } from '@/coreDomainsModule/entities/yearMonthRange';

// props

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectDisplayInventoryYearMonthRangeModal',
  props: {
    defaultStartYearMonth: {},
    defaultEndYearMonth: {},
    modalState: { type: Boolean }
  },
  emits: ["on-next", "on-back"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// emits
const emits = __emit;

//変数
const t = i18n.t;
const defaultYearMonth = DateService.convertDateToIYearMonth(new Date());
const locale = computed(() => i18n.locale);
const startYearMonth = ref<IYearMonth>(defaultYearMonth);
const endYearMonth = ref<IYearMonth>(defaultYearMonth);
const modalState = ref<boolean>(false);

// propsの値を初期値として設定
// モーダルの表示状態
watch(
  () => props.modalState,
  () => {
    modalState.value = props.modalState;
  },
  { immediate: true } // 初期値を即座に反映
);

// 開始年月の初期値
watch(
  () => props.defaultStartYearMonth,
  () => {
    startYearMonth.value = props.defaultStartYearMonth;
  },
  { immediate: true } // 初期値を即座に反映
);

// 終了年月の初期値
watch(
  () => props.defaultEndYearMonth,
  () => {
    endYearMonth.value = props.defaultEndYearMonth;
  },
  { immediate: true } // 初期値を即座に反映
);

const isDisabledApplyButton = computed(() => {
  return (
    startYearMonth.value.year === 0 || endYearMonth.value.year === 0 || startYearMonth.value.month === 0 || endYearMonth.value.month === 0
  );
});

/**
 * 内示遍歴ダウンロードモーダルの開始年月を選択した際の処理
 * @param selectedYearMonth - 選択された年月
 */
const onSelectedStartYearMonth = async (selectedYearMonth: IYearMonth) => {
  if (isDisabledYearMonth(selectedYearMonth)) {
    // 無効な範囲を選択した場合、エラートーストを表示
    toast.warning(t('10年前から2年後までの範囲で選択してください'));
    return;
  } else if (isStartYearMonthAfterEndYearMonth(selectedYearMonth)) {
    // 開始年月が終了年月よりも後の場合、エラートーストを表示
    toast.warning(t('開始年月は終了年月よりも前にしてください'));
    return;
  } else if (!isSelectedStartYearMonthValidRange(selectedYearMonth)) {
    // 開始年月が終了年月との範囲が24ヶ月以内でない場合、エラートーストを表示
    toast.warning(t('開始年月は終了年月から24ヶ月以内で選択してください'));
    return;
  }

  startYearMonth.value = selectedYearMonth;
};

/**
 * 内示遍歴ダウンロードモーダルの終了年月を選択した際の処理
 * @param selectedYearMonth - 選択された年月
 */
const onSelectedEndYearMonth = async (selectedYearMonth: IYearMonth) => {
  if (isDisabledYearMonth(selectedYearMonth)) {
    // 無効な範囲を選択した場合、エラートーストを表示
    toast.warning(t('10年前から2年後までの範囲で選択してください'));
    return;
  } else if (isEndYearMonthBeforeStartYearMonth(selectedYearMonth)) {
    // 終了年月が開始年月よりも前の場合、エラートーストを表示
    toast.warning(t('終了年月は開始年月よりも後にしてください'));
    return;
  } else if (!isSelectedEndYearMonthValidRange(selectedYearMonth)) {
    // 終了年月が開始年月との範囲が24ヶ月以内でない場合、エラートーストを表示
    toast.warning(t('終了年月は開始年月から24ヶ月以内で選択してください'));
    return;
  }

  endYearMonth.value = selectedYearMonth;
};

/**
 * 対象年月が無効かどうかを取得
 * @param selectedYearMonth - 選択された開始年月
 */
const isDisabledYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は(当月の120ヶ月前)以上、(当月の24ヶ月先)以下
  const currentYearMonth = DateService.convertDateToIYearMonth(new Date());
  const { updateYear: _headYear, updateMonth: _headMonth } = DateService.updateYearMonth(
    currentYearMonth.year,
    currentYearMonth.month,
    -120
  );
  const headYearMonth = { year: _headYear, month: _headMonth };
  const { updateYear: _tailYear, updateMonth: _tailMonth } = DateService.updateYearMonth(currentYearMonth.year, currentYearMonth.month, 24);
  const tailYearMonth = { year: _tailYear, month: _tailMonth };
  const betweenYearMonths = DateService.getYearMonthsBetween(headYearMonth, tailYearMonth);
  // 有効範囲外の場合は無効かつ、終了年月よりも後の年月を選択している場合は無効
  return !betweenYearMonths.some((yearMonth) => yearMonth.year === selectedYearMonth.year && yearMonth.month === selectedYearMonth.month);
};

/**
 * 開始年月が終了年月よりも後かどうかを取得
 * @param selectedYearMonth - 選択された開始年月
 */
const isStartYearMonthAfterEndYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 開始年月が選択されていない場合は無効
  if (endYearMonth.value.year === 0 || endYearMonth.value.month === 0) {
    return true;
  }

  // 終了年月が開始年月より前の場合は無効
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const endTotal = endYearMonth.value.year * 12 + endYearMonth.value.month;
  return endTotal - selectedTotal < 0;
};

/**
 * 終了年月が開始年月よりも前かどうかを判定
 * @param selectedYearMonth - 選択された開始年月
 */
const isEndYearMonthBeforeStartYearMonth = (selectedYearMonth: IYearMonth): boolean => {
  // 開始年月が選択されていない場合は無効
  if (startYearMonth.value.year === 0 || startYearMonth.value.month === 0) {
    return true;
  }

  // 終了年月が開始年月より前の場合は無効
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const startTotal = startYearMonth.value.year * 12 + startYearMonth.value.month;
  return selectedTotal - startTotal < 0;
};

/**
 * 開始年月選択時のの有効範囲をチェックする
 * @param selectedYearMonth - 選択された開始年月
 */
const isSelectedStartYearMonthValidRange = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は開始と終了の範囲が24ヶ月以内
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const endTotal = endYearMonth.value.year * 12 + endYearMonth.value.month;
  return Math.abs(endTotal - selectedTotal) <= DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE;
};

/**
 * 終了年月選択時のの有効範囲をチェックする
 * @param selectedYearMonth - 選択された終了年月
 */
const isSelectedEndYearMonthValidRange = (selectedYearMonth: IYearMonth): boolean => {
  // 有効範囲は開始と終了の範囲が24ヶ月以内
  const selectedTotal = selectedYearMonth.year * 12 + selectedYearMonth.month;
  const startTotal = startYearMonth.value.year * 12 + startYearMonth.value.month;
  return Math.abs(selectedTotal - startTotal) <= DISPLAY_INVENTORY_YEAR_MONTH_MAX_RANGE;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": modalState.value,
    "back-button-label": _unref(t)('キャンセル'),
    "next-button-label": _unref(t)('決定'),
    "is-disabled-next-button": isDisabledApplyButton.value,
    onOnBack: _cache[2] || (_cache[2] = ($event: any) => (emits('on-back'))),
    onOnNext: _cache[3] || (_cache[3] = ($event: any) => (emits('on-next', { startYearMonth: startYearMonth.value, endYearMonth: endYearMonth.value })))
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('表示範囲設定')), 1)
    ]),
    description: _withCtx(() => [
      _createElementVNode("div", null, _toDisplayString(_unref(t)('下記から表示期間を選択してください。')), 1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(MonthPicker, {
          class: "main-content__calendar download-timeline-calendar",
          "model-value": startYearMonth.value,
          locale: locale.value,
          "prop-format": _unref(t)('MonthPickerFormat'),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (onSelectedStartYearMonth($event)))
        }, null, 8, ["model-value", "locale", "prop-format"]),
        _createTextVNode(" " + _toDisplayString(_unref(t)('から')) + " ", 1),
        _createVNode(MonthPicker, {
          class: "main-content__calendar download-timeline-calendar",
          "model-value": endYearMonth.value,
          locale: locale.value,
          "prop-format": _unref(t)('MonthPickerFormat'),
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (onSelectedEndYearMonth($event)))
        }, null, 8, ["model-value", "locale", "prop-format"]),
        _createTextVNode(" " + _toDisplayString(_unref(t)('まで')), 1)
      ])
    ], undefined, true),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label", "is-disabled-next-button"]))
}
}

})